/**
 * Check-in, three states, plus the money.
 *
 * The states themselves live in CheckInToggle — the Start Tournament modal
 * renders the same control over the same endpoint, and two copies of it would
 * drift.
 *
 * A withdrawn entrant is MARKED, NOT MOVED. They keep the check_in_status they
 * had when they left — they really did check in — so they stay in that bucket,
 * in that count, with the toggle still live in case it needs correcting.
 * Withdrawal is a separate fact (tournament_entries.withdrawn_at, set only by
 * night-forfeit), and the row says so rather than the status column lying
 * about it.
 */

import { useState } from 'react';
import { apiPut } from '../../lib/api.js';
import CheckInToggle from './CheckInToggle.jsx';
import PaymentToggle from './PaymentToggle.jsx';

export default function CheckInTab({ venueId, tournamentId, tournament, entries, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError]   = useState(null);

  const base = `/api/venues/${venueId}/tournaments/${tournamentId}`;

  // Check-in is open from registration through play; the endpoint 409s outside
  // that, so the controls are disabled rather than left to fail.
  const open = tournament.status === 'registration' || tournament.status === 'active';

  const setStatus = async (entry, status) => {
    setBusyId(entry.id);
    setError(null);
    const { error: err } = await apiPut(`${base}/check-in`, { entryId: entry.id, status });
    setBusyId(null);
    if (err) { setError(err); return; }
    await onChanged();
  };

  const setPaid = async (entry, status) => {
    setBusyId(entry.id);
    setError(null);
    const { error: err } = await apiPut(`${base}/payments`, { entryId: entry.id, status });
    setBusyId(null);
    if (err) { setError(err); return; }
    await onChanged();
  };

  if (entries.length === 0) {
    return <div className="hd-empty">No entries to check in.</div>;
  }

  const counts = entries.reduce((acc, e) => {
    acc[e.check_in_status] = (acc[e.check_in_status] ?? 0) + 1;
    return acc;
  }, {});

  // Counted separately, NOT as a fourth bucket: a withdrawn player is still in
  // one of the three above, and subtracting them from it would make the three
  // numbers stop summing to the field.
  const withdrawnCount = entries.filter((e) => e.withdrawn_at).length;

  return (
    <div className="hd-stack">
      {error && <div className="hd-badge hd-badge-danger" role="alert">{error}</div>}

      <p className="hd-meta">
        {counts.checked_in ?? 0} in · {counts.running_late ?? 0} late
        {' · '}{counts.not_checked_in ?? 0} not in
        {withdrawnCount > 0 && ` · ${withdrawnCount} withdrawn`}
      </p>

      {!open && (
        <p className="hd-hint">
          Check-in is closed — it runs from registration through to the end of
          play (tournament status is '{tournament.status}').
        </p>
      )}

      <div className="hd-table-wrap">
        <table className="hd-table hd-admin-table">
          <thead>
            <tr>
              <th>Player</th>
              <th>Check-in</th>
              <th className="hd-table-right">Paid</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td>
                  {entry.display_name}
                  {entry.is_guest === 1 && <span className="hd-guest-tag">guest</span>}
                  {/* In the Player cell, not the Check-in cell: that column has
                      to go on showing their real status, and a marker sitting
                      in it would read as a fourth state of the toggle. */}
                  {entry.withdrawn_at && (
                    <span className="hd-badge hd-badge-danger" style={{ marginLeft: 8 }}>
                      Withdrawn
                    </span>
                  )}
                </td>
                <td>
                  <CheckInToggle
                    status={entry.check_in_status}
                    busy={busyId === entry.id}
                    disabled={!open}
                    onChange={(status) => setStatus(entry, status)}
                  />
                </td>
                <td className="hd-table-right">
                  <PaymentToggle
                    paid={entry.paid === 1}
                    busy={busyId === entry.id}
                    onChange={(status) => setPaid(entry, status)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
